import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DetailPanel, type PanelSpan } from './DetailPanelGrid';

export interface SingleValuePanelProps {
  title: string;
  value: string;
  footer?: string | undefined;
  valueTone?: 'primary' | 'neutral';
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
  className?: string | undefined;
  linkTo?: string | undefined;
  externalHref?: string | undefined;
  linkLabel?: string | undefined;
  linkTitle?: string | undefined;
  linkTooltip?: ReactNode | undefined;
  headerActions?: ReactNode | undefined;
}

const VALUE_TONE_CLASSES: Record<NonNullable<SingleValuePanelProps['valueTone']>, string> = {
  primary: 'text-backoffice-500',
  neutral: 'text-neutral-900',
};

export function SingleValuePanel({
  title,
  value,
  footer,
  valueTone = 'primary',
  panelId,
  currentSpan,
  className,
  linkTo,
  externalHref,
  linkLabel,
  linkTitle,
  linkTooltip,
  headerActions,
}: SingleValuePanelProps) {
  const effectiveSpan = currentSpan ?? '1x1';
  const isCompactSquare = effectiveSpan === '1x1';
  const content = (
    <div
      className={[
        'flex flex-col items-center justify-center text-center',
        isCompactSquare ? 'h-full min-h-0 gap-1' : 'min-h-[7rem] gap-3',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <p
        className={[
          'font-inter-tight font-semibold leading-none tabular-nums',
          isCompactSquare ? 'text-4xl' : 'text-5xl',
          VALUE_TONE_CLASSES[valueTone],
        ].join(' ')}
      >
        {value}
      </p>
      {footer ? (
        <p
          className={[
            'font-semibold text-neutral-600',
            isCompactSquare ? 'text-small leading-tight' : 'text-body',
          ].join(' ')}
        >
          {footer}
        </p>
      ) : null}
    </div>
  );
  const linkedContent = externalHref ? (
    <div className="group/single-value-link relative h-full">
      <a
        href={externalHref}
        target="_blank"
        rel="noreferrer"
        className="focus-ring block h-full rounded-md transition hover:bg-backoffice-50"
        aria-label={linkLabel}
        title={linkTitle}
      >
        {content}
      </a>
      {linkTooltip ? (
        <span className="pointer-events-none absolute left-1/2 top-2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white group-hover/single-value-link:inline-flex group-focus-within/single-value-link:inline-flex">
          {linkTooltip}
        </span>
      ) : null}
    </div>
  ) : linkTo ? (
    <Link
      to={linkTo}
      className="focus-ring block h-full rounded-md transition hover:bg-backoffice-50"
      aria-label={linkLabel}
      title={linkTitle}
    >
      {content}
    </Link>
  ) : (
    content
  );

  return (
    <DetailPanel
      title={title}
      span="1x1"
      panelId={panelId}
      currentSpan={currentSpan}
      className={className}
      headerActions={headerActions}
    >
      {linkedContent}
    </DetailPanel>
  );
}
